/**
 * The platform console's Knowledge candidates tab (L3, migration 0197): what each org's pipeline noticed the shared
 * foundry pack may lack, as generic statements, for Sahas staff to curate. The list counts plants and never names one;
 * a candidate's review names the orgs behind it (this console alone), says whether it shapes the pack yet (a count
 * needs 3 plants), and takes the decision: accept, reject with a reason, or mark landed with the PR's link. Accepting
 * shows the drafted pack change as text for a person to copy into a PR: nothing here writes the pack.
 *
 * The views are pure (rendered in tests without providers); KnowledgeCandidatesPanel holds the queries and mutations.
 */
import { useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronLeft, ChevronRight, ClipboardCopy, GitPullRequest, Lightbulb, Loader2, X } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/ui/card";
import { Input } from "@/lib/ui/input";
import { NativeSelect } from "@/lib/ui/native-select";
import { useToast } from "@/lib/ui/toast";
import { backendErrorMessage } from "@/lib/api";
import {
  knowledgeCandidatesApi,
  type KnowledgeCandidate,
  type KnowledgeCandidateDetail,
  type KnowledgeCandidateKind,
  type KnowledgeCandidateStatus,
} from "@/lib/api/knowledge-candidates";
import {
  KINDS,
  KIND_LABELS,
  STATUSES,
  STATUS_LABELS,
  STATUS_TONES,
  actionsFor,
  dayOf,
  decisionErrorText,
  decisionLine,
  pageText,
  plantsText,
  statementLines,
  validPrUrl,
  verdictWords,
} from "./knowledge-candidates";

/** Candidates a page of the list holds. */
const PAGE_SIZE = 50;

const TH = "whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-on-surface-muted";
const TD = "px-3 py-2 align-top text-sm";

export interface CandidateListViewProps {
  status: KnowledgeCandidateStatus | "";
  kind: KnowledgeCandidateKind | "";
  onStatus: (s: KnowledgeCandidateStatus | "") => void;
  onKind: (k: KnowledgeCandidateKind | "") => void;
  items: readonly KnowledgeCandidate[];
  total: number;
  /** Where the page begins, how many a page holds, where the next begins (null on the last page), and the move. */
  offset: number;
  pageSize: number;
  nextOffset: number | null;
  onPage: (offset: number) => void;
  loading: boolean;
  error: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

/** The candidates: each statement with its plants, first and last seen, and status. Counts only, never a name. */
export function CandidateListView(p: CandidateListViewProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-on-surface-muted">
          Status
          <NativeSelect
            aria-label="Status"
            className="h-9 w-48"
            value={p.status}
            onChange={(e) => p.onStatus(e.target.value as KnowledgeCandidateStatus | "")}
          >
            <option value="">Every status</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </NativeSelect>
        </label>
        <label className="flex flex-col gap-1 text-xs text-on-surface-muted">
          Kind
          <NativeSelect
            aria-label="Kind"
            className="h-9 w-64"
            value={p.kind}
            onChange={(e) => p.onKind(e.target.value as KnowledgeCandidateKind | "")}
          >
            <option value="">Every kind</option>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_LABELS[k]}
              </option>
            ))}
          </NativeSelect>
        </label>
        <span className="pb-2 text-xs text-on-surface-muted">
          {p.loading ? "Loading…" : pageText(p.offset, p.items.length, p.total)}
        </span>
        {!p.loading && (p.offset > 0 || p.nextOffset !== null) && (
          <div className="flex items-center gap-2 pb-1">
            <Button
              size="sm"
              variant="outline"
              aria-label="Previous page"
              disabled={p.offset === 0}
              onClick={() => p.onPage(Math.max(0, p.offset - p.pageSize))}
            >
              <ChevronLeft /> Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              aria-label="Next page"
              disabled={p.nextOffset === null}
              onClick={() => p.nextOffset !== null && p.onPage(p.nextOffset)}
            >
              Next <ChevronRight />
            </Button>
          </div>
        )}
      </div>
      {p.error && (
        <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger" role="alert">
          {p.error}
        </div>
      )}
      {!p.loading && !p.error && p.items.length === 0 ? (
        <p className="text-sm text-on-surface-muted">
          No candidates here. Each org&apos;s approved words, the defects and parameters the pack lacks, and the counts
          of orgs that consented arrive as they are noticed.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full" aria-label="Knowledge candidates">
            <thead className="border-b border-border bg-surface-muted/40">
              <tr>
                <th scope="col" className={TH}>
                  Statement
                </th>
                <th scope="col" className={TH}>
                  Kind
                </th>
                <th scope="col" className={TH}>
                  Plants
                </th>
                <th scope="col" className={TH}>
                  First seen
                </th>
                <th scope="col" className={TH}>
                  Last seen
                </th>
                <th scope="col" className={TH}>
                  Status
                </th>
                <th scope="col" className={TH}>
                  <span className="sr-only">Review</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {p.items.map((c) => (
                <tr
                  key={c.id}
                  className={
                    c.id === p.selectedId
                      ? "border-b border-border bg-brand-muted/30 last:border-b-0"
                      : "border-b border-border last:border-b-0"
                  }
                >
                  <td className={`${TD} text-on-surface`}>{c.summary}</td>
                  <td className={`${TD} whitespace-nowrap text-on-surface-muted`}>{KIND_LABELS[c.kind] ?? c.kind}</td>
                  <td className={TD}>
                    <div className="flex flex-col gap-1">
                      <span className="whitespace-nowrap tabular-nums text-on-surface">{plantsText(c)}</span>
                      {!c.shapes_pack && (
                        <Badge variant="warning" className="w-fit whitespace-nowrap text-[10px]">
                          Not yet shaping the pack
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className={`${TD} whitespace-nowrap tabular-nums text-on-surface-muted`}>{dayOf(c.first_seen_at)}</td>
                  <td className={`${TD} whitespace-nowrap tabular-nums text-on-surface-muted`}>{dayOf(c.last_seen_at)}</td>
                  <td className={TD}>
                    <Badge variant={STATUS_TONES[c.status]} className="whitespace-nowrap">
                      {STATUS_LABELS[c.status] ?? c.status}
                    </Badge>
                  </td>
                  <td className={`${TD} text-right`}>
                    <Button size="sm" variant="outline" onClick={() => p.onSelect(c.id)}>
                      Review
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export interface CandidateReviewViewProps {
  detail: KnowledgeCandidateDetail;
  note: string;
  onNote: (s: string) => void;
  prUrl: string;
  onPrUrl: (s: string) => void;
  busy: boolean;
  error: string;
  onAccept: () => void;
  onReject: () => void;
  onLand: () => void;
  onCopyDraft: () => void;
  onClose: () => void;
}

/** One candidate: its statement, the orgs behind it (this console alone names them), whether it shapes the pack,
 *  the decision, and once accepted the drafted pack change to copy into a PR. */
export function CandidateReviewView(p: CandidateReviewViewProps) {
  const d = p.detail;
  const can = actionsFor(d);
  const drafted = d.status === "accepted" || d.status === "landed";
  const verdicts = Object.entries(d.verdict_counts ?? {}).sort(([a], [b]) => a.localeCompare(b));
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <CardTitle className="text-base">{d.summary}</CardTitle>
          <div className="flex flex-wrap items-center gap-2 text-xs text-on-surface-muted">
            <span>{KIND_LABELS[d.kind] ?? d.kind}</span>
            <span aria-hidden>·</span>
            <Badge variant={STATUS_TONES[d.status]}>{STATUS_LABELS[d.status] ?? d.status}</Badge>
            <span aria-hidden>·</span>
            <span>
              first seen {dayOf(d.first_seen_at)}, last seen {dayOf(d.last_seen_at)}
            </span>
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={p.onClose} aria-label="Close the review">
          <X />
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <section className="flex flex-col gap-1">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-on-surface-muted">The statement</h3>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
            {statementLines(d.statement).map((l) => (
              <div key={l.key} className="contents">
                <dt className="text-on-surface-muted">{l.key}</dt>
                <dd className="font-mono text-on-surface">{l.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="flex flex-col gap-1">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-on-surface-muted">
            Plants: {plantsText(d)}
          </h3>
          <p className={d.shapes_pack ? "text-sm text-on-surface-muted" : "text-sm text-warning"}>{d.shaping}</p>
          {verdicts.length > 0 && (
            <p className="text-sm text-on-surface">
              Verdicts on the same subject:{" "}
              {verdicts.map(([v, n]) => `${verdictWords(v)} ${n}`).join(", ")}
            </p>
          )}
          <p className="text-xs text-on-surface-muted">
            The orgs behind it, named in this console only. The pack cites them by count, never by name.
          </p>
          <ul className="list-disc pl-5 text-sm text-on-surface" aria-label="Contributing orgs">
            {d.orgs.map((o) => (
              <li key={o.org_id}>
                {o.name || o.org_id}{" "}
                <span className="text-xs text-on-surface-muted">since {dayOf(o.contributed_at)}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-on-surface-muted">The decision</h3>
          {d.decided_at && (
            <p className="text-sm text-on-surface-muted">
              {STATUS_LABELS[d.status]} by {d.decided_by || "someone"} on {dayOf(d.decided_at)}
              {d.decision_note ? `: ${d.decision_note}` : ""}
              {d.pr_url ? (
                <>
                  {" "}
                  (
                  <a className="text-brand underline" href={d.pr_url} target="_blank" rel="noreferrer noopener">
                    the PR
                  </a>
                  )
                </>
              ) : null}
            </p>
          )}
          {d.decisions.length > 1 && (
            <div className="flex flex-col gap-1">
              <h4 className="text-xs font-medium text-on-surface-muted">Decision history</h4>
              <ul className="list-disc pl-5 text-sm text-on-surface-muted" aria-label="Decision history">
                {d.decisions.map((x) => (
                  <li key={`${x.at}-${x.action}`}>{decisionLine(x)}</li>
                ))}
              </ul>
            </div>
          )}
          {(can.accept || can.reject || can.land || can.acceptBlocked) && (
            <>
              <label className="flex flex-col gap-1 text-xs text-on-surface-muted">
                Note (a rejection says why)
                <textarea
                  aria-label="Decision note"
                  className="min-h-16 rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-on-surface focus:border-brand focus:outline-none"
                  maxLength={2000}
                  value={p.note}
                  onChange={(e) => p.onNote(e.target.value)}
                  disabled={p.busy}
                />
              </label>
              {can.land && (
                <label htmlFor="kc-pr-url" className="flex flex-col gap-1 text-xs text-on-surface-muted">
                  The PR that landed it (https)
                  <Input
                    id="kc-pr-url"
                    aria-label="PR link"
                    value={p.prUrl}
                    placeholder="https://github.com/…/pull/…"
                    onChange={(e) => p.onPrUrl(e.target.value)}
                    disabled={p.busy}
                  />
                </label>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {(can.accept || can.acceptBlocked) && (
                  <Button size="sm" onClick={p.onAccept} disabled={p.busy || !can.accept}>
                    <Check /> Accept and draft the pack change
                  </Button>
                )}
                {can.reject && (
                  <Button size="sm" variant="outline" onClick={p.onReject} disabled={p.busy || p.note.trim() === ""}>
                    <X /> Reject
                  </Button>
                )}
                {can.land && (
                  <Button size="sm" variant="secondary" onClick={p.onLand} disabled={p.busy || !validPrUrl(p.prUrl)}>
                    <GitPullRequest /> Mark landed
                  </Button>
                )}
                {p.busy && <Loader2 className="size-4 animate-spin text-on-surface-muted" aria-label="Saving" />}
              </div>
              {can.acceptBlocked && <p className="text-xs text-warning">{can.acceptBlocked}</p>}
            </>
          )}
          {p.error && (
            <p className="text-sm text-danger" role="alert">
              {p.error}
            </p>
          )}
        </section>

        {drafted ? (
          <section className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-on-surface-muted">
                The drafted pack change
              </h3>
              <Button size="sm" variant="outline" onClick={p.onCopyDraft}>
                <ClipboardCopy /> Copy
              </Button>
            </div>
            <p className="text-xs text-on-surface-muted">
              Copy it into a PR: the pack changes only by a reviewed PR with a version bump. Nothing here writes the pack.
            </p>
            <pre
              aria-label="Drafted pack change"
              className="max-h-96 overflow-auto rounded-md border border-border bg-surface-muted/40 p-3 font-mono text-xs text-on-surface"
            >
              {d.draft}
            </pre>
          </section>
        ) : (
          <details className="text-sm">
            <summary className="cursor-pointer text-on-surface-muted">Preview the pack change it would draft</summary>
            <pre className="mt-2 max-h-96 overflow-auto rounded-md border border-border bg-surface-muted/40 p-3 font-mono text-xs text-on-surface">
              {d.draft}
            </pre>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

/** The tab: the list, and the review of the candidate chosen. */
export function KnowledgeCandidatesPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState<KnowledgeCandidateStatus | "">("open");
  const [kind, setKind] = useState<KnowledgeCandidateKind | "">("");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [prUrl, setPrUrl] = useState("");
  const [decisionError, setDecisionError] = useState("");

  const listQ = useQuery({
    queryKey: ["knowledge-candidates", status, kind, offset],
    queryFn: () => knowledgeCandidatesApi.list({ status, kind, limit: PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });
  const detailQ = useQuery({
    queryKey: ["knowledge-candidate", selected],
    queryFn: () => knowledgeCandidatesApi.get(selected as string),
    enabled: selected !== null,
  });

  const decided = (d: KnowledgeCandidateDetail, words: string) => {
    qc.setQueryData(["knowledge-candidate", d.id], d);
    void qc.invalidateQueries({ queryKey: ["knowledge-candidates"] });
    setNote("");
    setPrUrl("");
    setDecisionError("");
    toast.push("ok", words);
  };
  const decide = useMutation({
    mutationFn: (v: { action: "accept" | "reject" | "land"; id: string }) =>
      v.action === "accept"
        ? knowledgeCandidatesApi.accept(v.id, note.trim())
        : v.action === "reject"
          ? knowledgeCandidatesApi.reject(v.id, note.trim())
          : knowledgeCandidatesApi.land(v.id, prUrl.trim(), note.trim()),
    onSuccess: (d, v) =>
      decided(
        d,
        v.action === "accept"
          ? "Accepted: the pack change is drafted below, to copy into a PR."
          : v.action === "reject"
            ? "Rejected, with the reason."
            : "Marked landed.",
      ),
    onError: (err) => setDecisionError(decisionErrorText(err)),
  });

  const select = (id: string) => {
    setSelected(id);
    setNote("");
    setPrUrl("");
    setDecisionError("");
  };
  const copyDraft = () => {
    const draft = detailQ.data?.draft ?? "";
    void navigator.clipboard?.writeText(draft).then(
      () => toast.push("ok", "The drafted pack change is copied."),
      () => toast.push("error", "The browser did not allow copying: select the text instead."),
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="flex flex-row items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-md bg-brand-muted text-brand">
            <Lightbulb className="size-5" />
          </div>
          <div className="flex flex-col">
            <CardTitle className="text-base">Knowledge candidates</CardTitle>
            <p className="text-xs text-on-surface-muted">
              What each plant&apos;s pipeline noticed the foundry pack may lack, as generic statements. Never a
              plant&apos;s figures. Counts come only from orgs that consented, and shape the pack from 3 plants.
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <CandidateListView
            status={status}
            kind={kind}
            onStatus={(s) => {
              setStatus(s);
              setOffset(0);
            }}
            onKind={(k) => {
              setKind(k);
              setOffset(0);
            }}
            items={listQ.data?.items ?? []}
            total={listQ.data?.total ?? 0}
            offset={listQ.data?.offset ?? offset}
            pageSize={PAGE_SIZE}
            nextOffset={listQ.data?.next_offset ?? null}
            onPage={setOffset}
            loading={listQ.isLoading}
            error={listQ.error ? backendErrorMessage(listQ.error, "no candidates") : ""}
            selectedId={selected}
            onSelect={select}
          />
        </CardContent>
      </Card>
      {selected !== null &&
        (detailQ.data && detailQ.data.id === selected ? (
          <CandidateReviewView
            detail={detailQ.data}
            note={note}
            onNote={setNote}
            prUrl={prUrl}
            onPrUrl={setPrUrl}
            busy={decide.isPending}
            error={decisionError}
            onAccept={() => decide.mutate({ action: "accept", id: selected })}
            onReject={() => decide.mutate({ action: "reject", id: selected })}
            onLand={() => decide.mutate({ action: "land", id: selected })}
            onCopyDraft={copyDraft}
            onClose={() => setSelected(null)}
          />
        ) : detailQ.error ? (
          <p className="text-sm text-danger" role="alert">
            {decisionErrorText(detailQ.error)}
          </p>
        ) : (
          <div className="flex items-center gap-2 text-sm text-on-surface-muted">
            <Loader2 className="size-4 animate-spin" /> Loading the candidate…
          </div>
        ))}
    </div>
  );
}
